import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ApiError } from '@/api/client'
import {
  attachOperator,
  deleteOperator,
  fetchMachineOperators,
  updateOperator,
} from '@/api/resources/machine-operators'
import { machinesQueryOptions } from '@/api/resources/machines'
import { organisationOperatorsQueryOptions } from '@/api/resources/organisation-operators'
import { LoadingSpinner } from '@/components/feedback/loading-spinner'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { OperatorMachinesDialog } from '@/components/users/operator-machines-dialog'
import { useRouteModal } from '@/hooks/use-route-modal'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_app/users/$organisationId/operators/$userId/edit')({
  head: () => ({ meta: [{ title: 'Operator - MyJunea' }] }),
  component: OperatorDetailRoute,
})

interface MachineAssignment {
  machineId: number
  emailOptin: boolean
}

function OperatorDetailRoute(): React.ReactElement {
  const { organisationId, userId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const organisationIdNum = Number(organisationId)
  const numericUserId = Number(userId)

  const { open, handleOpenChange } = useRouteModal(
    () =>
      void navigate({
        to: '/users/$organisationId/operators',
        params: { organisationId },
        resetScroll: false,
      }),
  )

  const { data: operators } = useQuery(organisationOperatorsQueryOptions(organisationIdNum))
  const operator = operators?.find((o) => o.id === numericUserId)

  const { data: allMachines } = useQuery(
    machinesQueryOptions({ organisation_id: [organisationIdNum] }),
  )

  const [assignments, setAssignments] = useState<MachineAssignment[] | null>(null)
  const [pendingMachineIds, setPendingMachineIds] = useState<Set<number>>(new Set())

  const initialAssignments = useMemo<MachineAssignment[]>(() => {
    if (!operator || !allMachines) return []
    const operatorMachineCodes = new Set(operator.machines.map((m) => m.code))
    return allMachines
      .filter((m) => operatorMachineCodes.has(m.code))
      .map((m) => ({ machineId: m.id, emailOptin: true }))
  }, [operator, allMachines])

  const effectiveAssignments = assignments ?? initialAssignments

  const invalidateQueries = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.organisationOperators.all(organisationIdNum),
    })
    void queryClient.invalidateQueries({ queryKey: queryKeys.machines.all })
  }, [queryClient, organisationIdNum])

  const addPending = useCallback((machineId: number) => {
    setPendingMachineIds((prev) => new Set([...prev, machineId]))
  }, [])

  const removePending = useCallback((machineId: number) => {
    setPendingMachineIds((prev) => {
      const next = new Set(prev)
      next.delete(machineId)
      return next
    })
  }, [])

  const handleToggleAssignment = useCallback(
    (machineId: number, machineCode: string) => {
      const current = assignments ?? initialAssignments
      const isAssigned = current.some((a) => a.machineId === machineId)

      if (isAssigned) {
        const remaining = current.filter((a) => a.machineId !== machineId)
        setAssignments(remaining)
        addPending(machineId)
        deleteOperator(machineCode, numericUserId)
          .then(() => {
            invalidateQueries()
            if (remaining.length === 0) {
              handleOpenChange(false)
              toast.success('Operator removed from all machines')
            }
          })
          .catch((error: unknown) => {
            setAssignments((prev) => [
              ...(prev ?? initialAssignments),
              { machineId, emailOptin: true },
            ])
            const message =
              error instanceof ApiError ? error.body.message : 'Failed to remove operator'
            toast.error(message)
            invalidateQueries()
          })
          .finally(() => {
            removePending(machineId)
          })
      } else {
        setAssignments([...current, { machineId, emailOptin: true }])
        addPending(machineId)
        attachOperator(machineCode, { user_id: numericUserId, email_optin: true })
          .then(() => {
            invalidateQueries()
          })
          .catch((error: unknown) => {
            setAssignments((prev) =>
              (prev ?? initialAssignments).filter((a) => a.machineId !== machineId),
            )
            const message =
              error instanceof ApiError ? error.body.message : 'Failed to add operator'
            toast.error(message)
            invalidateQueries()
          })
          .finally(() => {
            removePending(machineId)
          })
      }
    },
    [
      assignments,
      initialAssignments,
      numericUserId,
      addPending,
      removePending,
      invalidateQueries,
      handleOpenChange,
    ],
  )

  const handleToggleEmailOptin = useCallback(
    (machineId: number, machineCode: string) => {
      const current = assignments ?? initialAssignments
      const assignment = current.find((a) => a.machineId === machineId)
      if (!assignment) return

      const newOptin = !assignment.emailOptin
      setAssignments(
        current.map((a) => (a.machineId === machineId ? { ...a, emailOptin: newOptin } : a)),
      )
      addPending(machineId)

      fetchMachineOperators(machineCode)
        .then(async (ops) => {
          const op = ops.find((o) => o.id === numericUserId)
          const currentOptin = op?.email_optin ?? false
          return updateOperator(machineCode, numericUserId, { email_optin: !currentOptin })
        })
        .then(() => {
          invalidateQueries()
        })
        .catch((error: unknown) => {
          setAssignments((prev) =>
            (prev ?? initialAssignments).map((a) =>
              a.machineId === machineId ? { ...a, emailOptin: !newOptin } : a,
            ),
          )
          const message =
            error instanceof ApiError ? error.body.message : 'Failed to update notifications'
          toast.error(message)
          invalidateQueries()
        })
        .finally(() => {
          removePending(machineId)
        })
    },
    [assignments, initialAssignments, numericUserId, addPending, removePending, invalidateQueries],
  )

  const operatorName = operator ? `${operator.first_name} ${operator.last_name}` : 'Operator'

  if (!operator || !allMachines) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Operator</DialogTitle>
          </DialogHeader>
          <LoadingSpinner />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <OperatorMachinesDialog
      open={open}
      onOpenChange={handleOpenChange}
      operatorName={operatorName}
      machines={allMachines.map((m) => ({
        id: m.id,
        code: m.code,
        name: m.name,
        locationName: m.location?.name,
      }))}
      assignments={effectiveAssignments}
      pendingMachineIds={pendingMachineIds}
      onToggleAssignment={handleToggleAssignment}
      onToggleEmailOptin={handleToggleEmailOptin}
    />
  )
}
